<template>
  <v-dialog
    v-model="isVisible"
    :overlay="false"
    fullscreen
    scrollable
    transition="dialog-bottom-transition"
  >
    <v-card
      v-if="displayOrder && displayOrder?.Details"
      ref="cardOrderDetails"
      class="rounded-0"
    >
      <v-toolbar
        ref="cardOrderToolbar"
        class="secondary"
        flat
        height="80"
        max-height="80"
      >
        <v-avatar
          v-if="getOrderSourceTypeById(displayOrder.SourceTypeId)"
          :color="displayOrder.SourceTypeId < 100 ? 'blue-grey lighten-1' : 'white'"
        >
          <v-icon
            v-if="displayOrder.SourceTypeId < 100"
            dark
            medium
          >
            {{ getOrderSourceTypeById(displayOrder.SourceTypeId)?.logo }}
          </v-icon>

          <img
            v-else
            :src="getOrderSourceTypeById(displayOrder.SourceTypeId)?.logo"
          >
        </v-avatar>

        <v-toolbar-title class="pl-3">
          <div class="body-3 grey--text text--lighten-3">
            <span v-if="displayOrder.SourceTypeId >= 100">
              {{ $tc('Order.Title', 1) }}
            </span>

            {{
              displayOrder.SourceTypeId >= 100 ? getOrderSourceTypeById(displayOrder.SourceTypeId)?.desc : getOrderSourceTypeLangById(displayOrder.SourceTypeId)?.Desc
            }}

            <template
              v-if="parseInt(displayOrder.SourceTypeId) === 4 && displayOrder?.Details?.Table && displayOrder?.Details?.Table?.Table"
            >
              {{ displayOrder?.Details?.Table?.Table.TableAreaName }} #{{ displayOrder?.Details?.Table?.Table.TableName }}
            </template>
          </div>
          <div
            class="text-caption grey--text  text--lighten-1"
            style="cursor: pointer;"
            @click="copyToClipboard(displayOrder.OrderCode)"
          >
            {{ $t('Order.OrderDetailsDialog.Toolbar.Subtitle') }}{{ displayOrder.OrderCode }}
          </div>
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          dark
          icon
          @click="closeDialog"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text
        :class="isDarkMode ? 'grey darken-4' : 'grey lighten-4'"
        class="pa-0 fill-height"
        style="overflow-y: auto; height: calc(100vh - 220px);"
      >
        <v-container
          class="pa-0"
          fluid
        >
          <v-row no-gutters>
            <v-col
              cols="12"
              md="5"
            >
              <v-card
                class="transparent"
                flat
                height="100%"
              >
                <v-card-text
                  ref="cardOrderdetailsLeft"
                  class="pb-0"
                  style="overflow-y: auto;"
                >
                  <div class="text-center pt-1">
                    <barcode
                      :id="getBarcode(displayOrder)?.barcode"
                      :text="(String($tc('Order.Title',1) + ' #' + displayOrder.OrderCode).toLocaleUpperCase($i18n.locale))"
                      :value="getBarcode(displayOrder)?.barcode"
                      background="white"
                      font-size="12"
                      format="CODE128"
                      height="45"
                      text-margin="2"
                      text-position="bottom"
                      width="2"
                    />

                    <div v-if="displayOrder.DailyCheck && appConfig.LOCATION_DATA.ShowDailyCheck">
                      <v-chip
                        class="px-1"
                        label
                        x-small
                      >
                        {{ $t('Order.Headers.DailyCheck') }}: {{ displayOrder.DailyCheck }}
                      </v-chip>
                    </div>

                    <div
                      v-if="displayOrder.ExternalPlatform"
                      class="text-caption text-uppercase pb-1"
                    >
                      {{ getOrderSourceTypeById(displayOrder.ExternalSourceTypeId).desc }}: #{{ displayOrder.ExternalOrderId }}
                    </div>

                    <div
                      v-if="displayOrder.MarketplaceOrderCode"
                      class="text-caption text-uppercase pb-1"
                    >
                      {{ displayOrder.MarketplaceName }}: #{{ displayOrder.MarketplaceOrderCode }}
                    </div>

                    <div
                      v-if="displayOrder.QrData"
                      class="text-caption font-weight-bold pb-1"
                    >
                      {{ displayOrder.QrData }}
                    </div>

                    <template
                      v-if="displayOrder.StaffId && parseInt(displayOrder.SourceTypeId) === 4 && displayOrder?.Details?.Table && displayOrder?.Details?.Table?.Table"
                    >
                      <div class="pb-2 px-2 d-inline-block">
                        <strong>{{ $tc('Restaurant.Tables.Title', 1) }}</strong><br>
                        {{ displayOrder?.Details?.Table?.Table.TableAreaName }} #{{ displayOrder?.Details?.Table?.Table.TableName }}
                      </div>

                      <div class="pb-2 px-2 d-inline-block">
                        <strong>{{ $t('Common.SelectDateRangeDialog.Field.Staff.Label') }}</strong><br>{{ displayOrder.StaffFirstname }}
                        {{ displayOrder.StaffLastname }}
                      </div>

                      <div
                        v-if="displayOrder.Persons "
                        class="pb-2 px-2 d-inline-block"
                      >
                        <strong>{{ $t('Reservation.Table.Headers.Persons') }}</strong><br>
                        {{ displayOrder.Persons }}
                      </div>

                      <div
                        v-if="displayOrder.Persons && displayOrder.OrderTotal"
                        class="pb-2 px-2 d-inline-block"
                      >
                        <strong>{{ $t('Common.Misc.PricePerPerson') }}</strong><br>
                        {{ displayOrder.OrderTotal / displayOrder.Persons | currency }}
                      </div>
                    </template>

                    <print-button
                      v-if="printOrderDocumentVisible(displayOrder)"
                      hide-label
                      icon
                      menu-offset-y
                      menu-position="left"
                      open-on-hover
                      style="position: absolute; top: 25px; right: 25px;"
                      @printer-selected="printer => printOrder(displayOrder, printer)"
                    />
                  </div>

                  <v-divider class="mb-2" />

                  <template v-if="displayOrder?.Details?.Table?.Table">
                    <v-card flat>
                      <v-card-text>
                        <v-container
                          class="pa-0"
                          fluid
                        >
                          <v-row>
                            <v-col cols="12">
                              <v-tabs
                                v-model="tab"
                                :grow="true"
                                :slider-color="tabsSliderColor"
                                background-color="transparent"
                              >
                                <v-tab
                                  :disabled="!completedCount"
                                  href="#tab-orders"
                                >
                                  <v-badge
                                    :value="!!completedCount"
                                    color="blue"
                                    right
                                  >
                                    <template #badge>
                                      <span>
                                        {{ completedCount }}
                                      </span>
                                    </template>
                                    <span>{{ $t('Restaurant.Tables.Dialog.ViewTable.Tab.Orders.Title') }}</span>
                                  </v-badge>
                                </v-tab>

                                <v-tab
                                  :disabled="!unpaidCount"
                                  href="#tab-unpaid"
                                >
                                  <v-badge
                                    :value="!!unpaidCount"
                                    color="orange"
                                    right
                                  >
                                    <template #badge>
                                      <span>
                                        {{ unpaidCount }}
                                      </span>
                                    </template>
                                    <span>{{ $t('Restaurant.Tables.Dialog.ViewTable.Tab.Unpaid.Title') }}</span>
                                  </v-badge>
                                </v-tab>

                                <v-tab
                                  :disabled="!paidCount"
                                  href="#tab-paid"
                                >
                                  <v-badge
                                    :value="!!paidCount"
                                    color="green"
                                    right
                                  >
                                    <template #badge>
                                      <span>
                                        {{ paidCount }}
                                      </span>
                                    </template>
                                    <span>{{ $t('Restaurant.Tables.Dialog.ViewTable.Tab.Paid.Title') }}</span>
                                  </v-badge>
                                </v-tab>

                                <v-tab
                                  :disabled="!canceledCount"
                                  href="#tab-canceled"
                                >
                                  <v-badge
                                    :value="!!canceledCount"
                                    color="red"
                                    right
                                  >
                                    <template #badge>
                                      <span>
                                        {{ canceledCount }}
                                      </span>
                                    </template>
                                    <span>{{ $t('Restaurant.Tables.Dialog.ViewTable.Tab.Canceled.Title') }}</span>
                                  </v-badge>
                                </v-tab>
                              </v-tabs>
                            </v-col>

                            <v-col cols="12">
                              <v-tabs-items v-model="tab">
                                <v-tab-item value="tab-orders">
                                  <table-completed-orders-list
                                    :order-id="displayOrder.Id"
                                    :print="true"
                                    :print-order="false"
                                    :print-order-preparation="false"
                                    :print-receipt="parseInt(displayOrder.StatusId) !== 19"
                                    :table="displayOrder?.Details?.Table?.Table"
                                    :view-data="displayOrder?.Details?.Table"
                                    desc
                                  />
                                </v-tab-item>

                                <v-tab-item value="tab-unpaid">
                                  <table-completed-orders-list
                                    :table="displayOrder?.Details?.Table?.Table"
                                    :view-data="displayOrder?.Details?.Table"
                                    desc
                                    unpaid
                                  />
                                </v-tab-item>

                                <v-tab-item value="tab-paid">
                                  <table-payments-list
                                    :table="displayOrder?.Details?.Table?.Table"
                                    :view-data="displayOrder?.Details?.Table"
                                    desc
                                  />
                                </v-tab-item>

                                <v-tab-item value="tab-canceled">
                                  <table-completed-orders-list
                                    :print="false"
                                    :table="displayOrder?.Details?.Table?.Table"
                                    :view-data="displayOrder?.Details?.Table"
                                    canceled
                                    desc
                                  />
                                </v-tab-item>
                              </v-tabs-items>
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-card-text>
                    </v-card>
                  </template>

                  <template v-else>
                    <template v-for="category in displayOrder.Details.CartItemsWithCategories">
                      <div
                        :key="category.Category.Id"
                        :style="'border-left: #' + (category.Category.Color ? category.Category.Color : 'e1e1e1') + ' 3px solid; ' + (isDarkMode ? 'background-color: #191919;' : 'background-color: #e1e1e1;')"
                        class="mt-2"
                      >
                        <v-subheader class="text-subtitle-1 pl-4 pt-2 pb-2">
                          {{ getTranslatedField(category.Category, 'Name') }}
                        </v-subheader>
                      </div>

                      <template v-for="(item, i) in category.Items">
                        <div :key="item.Id + '_' + i + '_' + category.Category.Id">
                          <div
                            class="text-body-2 font-weight-medium pa-1"
                            style="display: table;"
                          >
                            <div style="display: table-row-group; vertical-align: middle;">
                              <template v-if="item.UnitId > 1">
                                <span
                                  class="float-left orange--text mr-1"
                                  style="display: table-cell; vertical-align: middle; white-space: nowrap; min-width: 48px;"
                                >
                                  {{ formatUnitQuantity($i18n.locale, item.UnitQuantity, getMeasurementUnitById(item.UnitId)) }}
                                </span>
                              </template>
                              <template v-else>
                                <span
                                  class="float-left orange--text mr-1"
                                  style="display: table-cell; vertical-align: middle; white-space: nowrap; min-width: 28px;"
                                >
                                  {{ item.Quantity }} x
                                </span>
                              </template>
                              <span style="display: table-cell; vertical-align: middle; width: 100%;">
                                {{ getTranslatedField(item, 'Name') }}
                              </span>
                              <span
                                class="float-right ml-2"
                                style="display: table-cell; vertical-align: middle; white-space: nowrap;"
                              >
                                {{ item.SubTotal | currency }}
                              </span>
                            </div>
                          </div>

                          <div
                            v-if="item.OptionsWithGroups && Array.isArray(item.OptionsWithGroups) && item.OptionsWithGroups.length"
                            class="grey--text text--darken-1 ml-1 text-caption"
                          >
                            <template v-for="(optionGroup, idx) in item.OptionsWithGroups">
                              <div
                                :key="idx"
                                style="display: table-row-group; vertical-align: middle;"
                              >
                                <div
                                  :style="`min-width:${item.UnitId > 1 ? 48 : 28}px`"
                                  style="display: table-cell; vertical-align: middle; white-space: nowrap;"
                                />
                                <div
                                  style="display: table-cell; vertical-align: middle; white-space: nowrap; width: 100%;"
                                >
                                  <div
                                    v-if="optionGroup.display"
                                    class="text-caption font-weight-bold pt-2 pb-1 grey--text text--darken-2 ml-1"
                                  >
                                    {{
                                      optionGroup.group[$i18n.locale] || optionGroup.group[defaultLanguage.Code] || optionGroup.group[Object.keys(optionGroup.group) && Object.keys(optionGroup.group)[0]]
                                    }}
                                  </div>
                                  <template
                                    v-for="(option, odx) in (optionGroup.options[$i18n.locale] || optionGroup.options[defaultLanguage.Code] || optionGroup.options[Object.keys(optionGroup.options) && Object.keys(optionGroup.options)[0]])"
                                  >
                                    <div
                                      :key="odx"
                                      style="margin-left: 5px !important;"
                                    >
                                      • <span v-safe-html="option" />
                                    </div>
                                  </template>
                                </div>
                              </div>
                            </template>
                          </div>

                          <div
                            v-if="item.Comment"
                            :class="isDarkMode ? 'white--text' : 'black--text'"
                            class="ml-1 text-caption"
                          >
                            {{ $t('Order.OrderDetailsDialog.Customer.Comments') }}
                            <span class="grey--text text--darken-1">
                              {{ item.Comment }}
                            </span>
                          </div>

                          <div v-if="orderCartItemHasTotals(item)">
                            <template v-for="(itemCoupon, itemCouponIdx) in getOrderCartItemCoupons(item)">
                              <div
                                :key="`${item.Id}-${i}-${category.Category.Id}-coupon-${itemCouponIdx}`"
                                class="text-caption grey--text text--darken-2 px-1"
                                style="display: table;"
                              >
                                <div style="display: table-row-group; vertical-align: middle;">
                                  <span
                                    class="float-left orange--text mr-1"
                                    style="display: table-cell; vertical-align: middle; white-space: nowrap; min-width: 28px;"
                                  >
                                    <v-icon
                                      color="purple"
                                      small
                                    >
                                      redeem
                                    </v-icon>
                                  </span>

                                  <span style="display: table-cell; vertical-align: middle; width: 100%;">
                                    {{ $t('Common.Misc.Discount').toLocaleUpperCase($i18n.locale) }} <span v-if="itemCoupon.Custom">({{ itemCoupon.Custom }})</span>
                                  </span>
                                  <span
                                    class="float-right ml-2"
                                    style="display: table-cell; vertical-align: middle; white-space: nowrap;"
                                  >
                                    {{ itemCoupon.Value | currency }}
                                  </span>
                                </div>
                              </div>
                            </template>

                            <template v-for="(itemExtraCharge, itemExtraChargeIdx) in getOrderCartItemExtraCharges(item)">
                              <div
                                :key="`${item.Id}-${i}-${category.Category.Id}-extra-charge-${itemExtraChargeIdx}`"
                                class="text-caption grey--text text--darken-2 px-1"
                                style="display: table;"
                              >
                                <div style="display: table-row-group; vertical-align: middle;">
                                  <span
                                    class="float-left orange--text mr-1"
                                    style="display: table-cell; vertical-align: middle; white-space: nowrap; min-width: 28px;"
                                  >
                                    {{ itemExtraCharge.Quantity }} x
                                  </span>

                                  <span style="display: table-cell; vertical-align: middle; width: 100%;">
                                    {{ getTranslatedField(itemExtraCharge, 'Name') }}
                                  </span>
                                  <span
                                    class="float-right ml-2"
                                    style="display: table-cell; vertical-align: middle; white-space: nowrap;"
                                  >
                                    +{{ itemExtraCharge.Value | currency }}
                                  </span>
                                </div>
                              </div>
                            </template>
                          </div>
                        </div>
                        <v-divider
                          :key="item.Id + '_' + i + '_' + category.Category.Id + '_divider'"
                          class="my-2"
                        />
                      </template>
                    </template>
                  </template>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col
              cols="12"
              md="7"
            >
              <v-card
                :class="isDarkMode ? 'grey darken-4' : 'grey lighten-4'"
                class="py-4"
                flat
              >
                <v-card-text
                  ref="cardOrderDetailsRight"
                  :style="isDarkMode ? 'background-color: #1e1e1e' : 'white'"
                  style="overflow-y: auto;"
                >
                  <v-container
                    class="pa-0"
                    fluid
                  >
                    <v-row no-gutters>
                      <template
                        v-if="markerAddress.Type === AddressTypeEnum.DEFAULT && !isCustomAddress(displayOrder.Details.CustomerAddress) && !isQrOrder(displayOrder) && parseInt(displayOrder.OrderTypeId) === 1 && parseInt(displayOrder.AddressId) > -1 && displayOrder.Details.CustomerAddress"
                      >
                        <v-col cols="12">
                          <v-map
                            v-if="mapVisible"
                            :address-marker="markerAddress"
                            :map-center="mapCenter"
                            height="350px"
                            show-address-marker
                            show-store-marker
                          />
                        </v-col>

                        <v-col
                          class="mb-2 mt-2"
                          cols="12"
                        >
                          <v-divider />
                        </v-col>
                      </template>

                      <template v-if="parseInt(displayOrder.OrderTypeId) === 1">
                        <v-col
                          v-if="parseInt(displayOrder.AddressId) > -1 && displayOrder.Details.CustomerAddress && displayOrder.Details.CustomerAddress.Address"
                          cols="12"
                          sm="6"
                        >
                          <div class="grey--text text--darken-1">
                            <span :class="isDarkMode ? 'white--text' : 'black--text'">
                              {{ $t('Order.OrderDetailsDialog.Customer.Address.Street') }}
                            </span> {{
                              displayOrder.Details.CustomerAddress.Address + ' ' + (displayOrder.Details.CustomerAddress.Number || '')
                            }}
                          </div>
                        </v-col>
                        <v-col
                          v-if="parseInt(displayOrder.AddressId) > -1 && displayOrder.Details.CustomerAddress && (displayOrder.Details.CustomerAddress.City || displayOrder.Details.CustomerAddress.Postcode)"
                          cols="12"
                          sm="6"
                        >
                          <div class="grey--text text--darken-1">
                            <template v-if="displayOrder.Details.CustomerAddress.City">
                              <span :class="isDarkMode ? 'white--text' : 'black--text'">
                                {{ $t('Order.OrderDetailsDialog.Customer.Address.City') }}
                              </span> {{ displayOrder.Details.CustomerAddress.City }}
                            </template>

                            <template v-if="displayOrder.Details.CustomerAddress.Postcode">
                              <span
                                :class="isDarkMode ? 'white--text' : 'black--text'"
                                class="pl-2"
                              >
                                {{ $t('Order.OrderDetailsDialog.Customer.Address.PostCode') }}
                              </span> {{ displayOrder.Details.CustomerAddress.Postcode }}
                            </template>
                          </div>
                        </v-col>

                        <v-col
                          v-if="parseInt(displayOrder.AddressId)>-1 && displayOrder.Details.CustomerAddress && displayOrder.Details.CustomerAddress.DoorBell"
                          cols="12"
                          sm="6"
                        >
                          <div class="grey--text text--darken-1">
                            <span :class="isDarkMode ? 'white--text' : 'black--text'">
                              {{ $t('Order.OrderDetailsDialog.Customer.Address.Bell') }}
                            </span> {{ displayOrder.Details.CustomerAddress.DoorBell }}
                          </div>
                        </v-col>

                        <v-col
                          v-if="parseInt(displayOrder.AddressId)>-1 && displayOrder.Details.CustomerAddress && displayOrder.Details.CustomerAddress.Floor"
                          cols="12"
                          sm="6"
                        >
                          <div class="grey--text text--darken-1">
                            <span :class="isDarkMode ? 'white--text' : 'black--text'">
                              {{ $t('Order.OrderDetailsDialog.Customer.Address.Floor') }}
                            </span> {{ displayOrder.Details.CustomerAddress.Floor }}
                          </div>
                        </v-col>

                        <template v-if="displayOrder.Details.CustomerAddress && displayOrder.Details.CustomerAddress.ExtraFieldsTranslated">
                          <template v-for="(field, index) in displayOrder.Details.CustomerAddress.ExtraFieldsTranslated">
                            <v-col
                              :key="`customer-address-${displayOrder.AddressId}-field-${field.Key}-${index}`"
                              cols="12"
                              sm="6"
                            >
                              <div class="grey--text text--darken-1">
                                <span :class="isDarkMode ? 'white--text' : 'black--text'">
                                  {{ field.Name }}
                                </span> {{ field.Value }}
                              </div>
                            </v-col>
                          </template>
                        </template>

                        <v-col
                          v-if="displayOrder.Details.CustomerAddress && displayOrder.Details.CustomerAddress.Comment"
                          cols="12"
                        >
                          <div class="grey--text text--darken-1">
                            <span :class="isDarkMode ? 'white--text' : 'black--text'">
                              {{ $t('Order.OrderDetailsDialog.Customer.Address.Comment') }}
                            </span> {{ displayOrder.Details.CustomerAddress.Comment }}
                          </div>
                        </v-col>
                      </template>

                      <v-col
                        v-if="parseInt(displayOrder.AddressId)>-1 && displayOrder.Details.CustomerAddress && !isQrOrder(displayOrder)"
                        class="mt-2"
                        cols="12"
                      >
                        <v-divider />
                      </v-col>

                      <v-col
                        class="mt-2"
                        cols="12"
                        sm="6"
                      >
                        <div class="grey--text text--darken-1">
                          <span :class="isDarkMode ? 'white--text' : 'black--text'">
                            {{ $t('Order.OrderDetailsDialog.Customer.Name') }}
                          </span>

                          <v-icon
                            :class="[parseInt(displayOrder.CustomerId) === 0 ? 'grey--text' : 'amber--text']"
                            class="text--lighten-2"
                            small
                          >
                            verified_user
                          </v-icon>

                          <span
                            v-if="posUserHasComponentPermission('CustomerDetails', 'ACCESS') && parseInt(displayOrder.CustomerId) !== 0"
                            :class="isDarkMode ? 'grey--text text--lighten-1' : 'grey--text text--darken-3'"
                            style="cursor: pointer;"
                            @click="$bus.$emit('show-customer-details-dialog', displayOrder.CustomerId)"
                          >
                            {{ displayOrder.FullName }}
                          </span>

                          <template v-else>
                            <span :class="isDarkMode ? 'grey--text text--lighten-1' : 'grey--text text--darken-3'">
                              {{
                                parseInt(displayOrder.CustomerId) !== 0 || displayOrder.FullName.trim() ? displayOrder.FullName : $t('Customer.CustomerType.Visitor')
                              }}
                            </span>
                          </template>
                        </div>
                      </v-col>

                      <v-col
                        v-if="displayOrder.Email && !emailIsGuest(displayOrder.Email)"
                        class="mt-2"
                        cols="12"
                        sm="6"
                      >
                        <div class="grey--text text--darken-1">
                          <span :class="isDarkMode ? 'white--text' : 'black--text'">
                            {{ $t('Order.OrderDetailsDialog.Customer.Email') }}:
                          </span> {{ displayOrder.Email }}
                        </div>
                      </v-col>

                      <v-col
                        v-if="displayOrder.Telephone && $options.filters.phone(displayOrder.Telephone)"
                        class="mt-2"
                        cols="12"
                        sm="6"
                      >
                        <div
                          class="grey--text text--darken-1"
                          style="white-space: nowrap;"
                        >
                          <span :class="isDarkMode ? 'white--text' : 'black--text'">
                            {{ $t('Order.OrderDetailsDialog.Customer.Phone') }}
                          </span> {{ displayOrder.Telephone | phone }}
                        </div>
                      </v-col>

                      <v-col
                        v-if="displayOrder.Telephone2 && $options.filters.phone(displayOrder.Telephone2)"
                        class="mt-2"
                        cols="12"
                        sm="6"
                      >
                        <div
                          class="grey--text text--darken-1"
                          style="white-space: nowrap;"
                        >
                          <span :class="isDarkMode ? 'white--text' : 'black--text'">
                            {{ $t('Order.OrderDetailsDialog.Customer.Phone') }}
                          </span> {{ displayOrder.Telephone2 | phone }}
                        </div>
                      </v-col>

                      <v-col
                        v-if="displayOrder.Telephone3 && $options.filters.phone(displayOrder.Telephone3)"
                        class="mt-2"
                        cols="12"
                        sm="6"
                      >
                        <div
                          class="grey--text text--darken-1"
                          style="white-space: nowrap;"
                        >
                          <span :class="isDarkMode ? 'white--text' : 'black--text'">
                            {{ $t('Order.OrderDetailsDialog.Customer.Phone') }}
                          </span> {{ displayOrder.Telephone3 | phone }}
                        </div>
                      </v-col>

                      <v-col
                        class="mt-2"
                        cols="12"
                      >
                        <v-divider />
                      </v-col>

                      <v-col
                        class="mt-2"
                        cols="12"
                        sm="6"
                      >
                        <div class="grey--text text--darken-1">
                          <span :class="isDarkMode ? 'white--text' : 'black--text'">
                            {{ $t('Common.Date.Time').toLocaleUpperCase($i18n.locale) }}:
                          </span> {{ displayOrder.IsScheduled ? displayOrder.ScheduledTimeAt : displayOrder.TimeAdded }}
                        </div>
                      </v-col>

                      <v-col
                        class="mt-2"
                        cols="12"
                        sm="6"
                      >
                        <div class="grey--text text--darken-1">
                          <span :class="isDarkMode ? 'white--text' : 'black--text'">
                            {{ $t('Common.Date.DateShort').toLocaleUpperCase($i18n.locale) }}:
                          </span> {{ displayOrder.IsScheduled ? displayOrder.ScheduledDateAt : displayOrder.DateAdded }}
                        </div>
                      </v-col>

                      <v-col
                        class="mt-2"
                        cols="12"
                      >
                        <v-divider />
                      </v-col>

                      <v-col
                        v-if="displayOrder.Comment"
                        class="mt-2"
                        cols="12"
                      >
                        <div class="grey--text text--darken-1">
                          <span :class="isDarkMode ? 'white--text' : 'black--text'">
                            {{ $t('Order.OrderDetailsDialog.Customer.Comments') }}
                          </span> {{ displayOrder.Comment }}
                        </div>
                      </v-col>

                      <v-col
                        v-if="displayOrder.Comment"
                        class="mt-2"
                        cols="12"
                      >
                        <v-divider />
                      </v-col>
                    </v-row>

                    <v-row dense>
                      <v-col cols="auto">
                        <div class="mt-2">
                          <order-status-chip
                            :order="displayOrder"
                            min-width="185"
                          />
                        </div>
                      </v-col>

                      <v-col cols="auto">
                        <div class="mt-2">
                          <order-type-chip
                            :order="displayOrder"
                            label
                            min-width="185"
                          />
                        </div>
                      </v-col>

                      <v-col cols="auto">
                        <div class="mt-2">
                          <order-delivery-status-chip
                            :order="displayOrder"
                            min-width="185"
                          />
                        </div>
                      </v-col>

                      <v-col cols="auto">
                        <div class="mt-2">
                          <order-payments-chip
                            :item="displayOrder"
                            min-width="185"
                          />
                        </div>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>

              <v-card
                v-if="userCanViewFiscalDoc && (isSelectedOrderToday || selectedOrderHasFiscalDocuments)"
                :style="isDarkMode ? 'background-color: #1e1e1e' : 'white'"
                class="mb-4"
                flat
              >
                <v-card-title
                  class="font-weight-bold py-1"
                  style="min-height: 53px;"
                >
                  {{ $t('Order.OrderDetailsDialog.Receipt.Title') }}

                  <v-spacer />

                  <issue-receipt-document-button
                    v-if="isVisible && issueReceiptDocumentVisible(displayOrder) && !userIsDelivery && displayOrder.Details && (orderCanGenerateFiscalDocument(displayOrder) || (!orderCanGenerateFiscalDocument(displayOrder) && orderHasFiscalDocument(displayOrder)))"
                    :can-cancel-document="posUserCan('RECEIPTS', 'MANAGE')"
                    :can-convert-receipt="isSelectedOrderToday && appCanConvertReceiptToInvoice"
                    :can-issue-document="orderCanGenerateFiscalDocument(displayOrder)"
                    :can-issue-receipt="orderCanGenerateFiscalDocument(displayOrder)"
                    :document-id="orderLatestFiscalDocument(displayOrder)?.ReceiptId"
                    :document-type="orderLatestFiscalDocument(displayOrder)?.ReceiptType"
                    :document-url="orderLatestFiscalDocument(displayOrder)?.DocumentUrl"
                    :from="EmailNotifyFromEnum.ORDER"
                    :icon-name="orderLatestFiscalDocument(displayOrder)?.ReceiptId && !orderCanGenerateFiscalDocument(displayOrder) ? 'mdi-receipt-text-edit-outline' : 'mdi-receipt-text-plus-outline'"
                    :invoice="appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.INVOICE) && appHasInvoiceDocEnabled"
                    :label="orderLatestFiscalDocument(displayOrder)?.ReceiptId && !orderCanGenerateFiscalDocument(displayOrder) ? $t('Common.Button.ManageDocument').toLocaleUpperCase($i18n.locale) : $t('Common.Button.IssueDocument').toLocaleUpperCase($i18n.locale)"
                    :receipt="appFiscalDocsProviderDocument.includes(FiscalDocTypeEnum.RECEIPT) && appHasReceiptDocEnabled"
                    :tooltip="orderLatestFiscalDocument(displayOrder)?.ReceiptId && !orderCanGenerateFiscalDocument(displayOrder) ? orderHasReceipt(displayOrder) ? $t('Common.Button.ManageReceipt') : orderHasInvoice(displayOrder) ? $t('Common.Button.ManageInvoice') : $t('Common.Button.ManageFnb') : $t('Common.Button.IssueDocument')"
                    :tooltip-open-delay="1500"
                    depressed
                    small
                    tooltip-position="top"
                    @view-document="data => $bus.$emit('fiscal-document:view', data.DocUrl)"
                    @issue-document="data => $bus.$emit('fiscal-document:create', displayOrder, data.DocType, (data.Print === false || data.Print === true) ? {Print: data.Print} : null)"
                    @cancel-document="data => $bus.$emit('fiscal-document:cancel', orderLatestFiscalDocument(displayOrder), data)"
                    @print-document="data => $bus.$emit('fiscal-document:print', orderLatestFiscalDocument(displayOrder)?.ReceiptId)"
                    @email-document="data => $bus.$emit('fiscal-document:email', orderLatestFiscalDocument(displayOrder)?.ReceiptId, displayOrder, data)"
                    @convert-receipt="data => $bus.$emit('fiscal-document:convert-to-invoice', orderLatestFiscalDocument(displayOrder), data)"
                  />
                </v-card-title>

                <v-divider />

                <v-card-text
                  ref="cardOrderDetailsReceipts"
                  class=""
                  style="overflow-y: auto;"
                >
                  <receipts-table
                    :cancel="userCanViewFiscalDoc"
                    :convert="isSelectedOrderToday && appCanConvertReceiptToInvoice"
                    :email="parseInt(displayOrder.StatusId) !== OrderStatusEnum.Canceled && appFiscalDocsProviderNotify.includes(NotifyTypeEnum.EMAIL)"
                    :order="displayOrder"
                    :orders="ordersList"
                    :print="parseInt(displayOrder.StatusId) !== OrderStatusEnum.Canceled && appMachineHasFiscalDocPrinters"
                    :receipts="displayOrder.Details.OrderReceipts"
                    alternate-group-colors
                  />
                </v-card-text>
              </v-card>

              <v-card
                v-if="isSelectedOrderToday || selectedOrderHasTransactions"
                :style="isDarkMode ? 'background-color: #1e1e1e' : 'white'"
                class="mb-4"
                flat
              >
                <v-card-title
                  class="font-weight-bold py-1 px-2"
                  style="min-height: 53px;"
                >
                  <v-tabs
                    v-model="transactionsTab"
                    :color="$vuetify.theme.dark ? 'white' : 'black'"
                    slider-color="primary"
                  >
                    <v-tab
                      class="text-capitalize text-h6 font-weight-bold px-2 mr-4"
                      href="#tab-transactions-active"
                    >
                      {{ $t('Order.OrderDetailsDialog.Transaction.Title') }}
                    </v-tab>

                    <v-tab
                      v-if="!!displayOrder.Details?.TransactionsHistory?.length"
                      class="text-capitalize text-h6 font-weight-bold px-2 mr-4"
                      href="#tab-transactions-history"
                    >
                      {{ $t('Order.OrderDetailsDialog.Transaction.History') }}
                    </v-tab>
                  </v-tabs>
                </v-card-title>

                <v-divider />

                <v-card-text
                  ref="cardOrderDetailsTransactions"
                >
                  <v-tabs-items v-model="transactionsTab">
                    <v-tab-item value="tab-transactions-active">
                      <order-payment-transaction-table
                        :order="displayOrder"
                        :transactions="displayOrder.Details.Payments"
                      />
                    </v-tab-item>

                    <v-tab-item
                      v-if="!!displayOrder.Details?.TransactionsHistory?.length"
                      value="tab-transactions-history"
                    >
                      <order-payment-transaction-history-table
                        :order="displayOrder"
                        :transactions="displayOrder.Details?.TransactionsHistory"
                      />
                    </v-tab-item>
                  </v-tabs-items>
                </v-card-text>
              </v-card>

              <v-card
                v-if="canShowNotificationsTable"
                :style="isDarkMode ? 'background-color: #1e1e1e' : 'white'"
                class="mb-4"
                flat
              >
                <v-card-title
                  class="font-weight-bold py-1"
                  style="min-height: 53px;"
                >
                  {{ $t('Order.OrderDetailsDialog.Notification.Title') }}

                  <v-spacer />

                  <div class="text-end">
                    <template v-if="canNotifyForStatus">
                      <v-tooltip
                        color="blue darken-3"
                        min-width="150"
                        top
                      >
                        <template #activator="{on}">
                          <v-btn
                            :disabled="displayOrder.notifyStatusIsLoading || !displayOrder.Details.CanNotify"
                            :loading="displayOrder.notifyStatusIsLoading"
                            class="mt-1 ml-2 pl-0 pr-1 white--text"
                            color="blue darken-3"
                            ripple
                            small
                            style="min-width: 0; height: 32px;"
                            v-on="on"
                            @click.native.stop="orderNotifyCustomerOrderStatus(displayOrder, OrderNotifyFromEnum.ORDER_DETAILS)"
                          >
                            <v-avatar
                              class="rounded-l"
                              size="32"
                              tile
                            >
                              <v-icon
                                :class="displayOrder.Details.StatusNotifyAt ? 'success' : 'blue lighten-2'"
                                small
                              >
                                {{ displayOrder.Details.StatusNotifyAt ? 'mdi-check-bold' : 'mdi-send' }}
                              </v-icon>
                            </v-avatar>

                            <div class="mx-1">
                              {{ $t('Common.Button.NotifyStatus') }}
                            </div>
                          </v-btn>
                        </template>

                        <span>{{ $t('Settings.Notification.Data.Delivery.TooltipStatus') }}</span>
                      </v-tooltip>
                    </template>

                    <template v-if="canNotifyForCollection">
                      <v-tooltip
                        color="purple"
                        min-width="150"
                        top
                      >
                        <template #activator="{on}">
                          <v-btn
                            :disabled="displayOrder.notifyDeliveryIsLoading || !displayOrder.Details.CanNotifyDelivery || displayOrder.StatusId === OrderStatusEnum.Canceled"
                            :loading="displayOrder.notifyDeliveryIsLoading"
                            class="mt-1 ml-2 pl-0 pr-1 white--text"
                            color="purple"
                            ripple
                            small
                            style="min-width: 0; height: 32px;"
                            v-on="on"
                            @click.native.stop="orderNotifyCustomerOrderCollect(displayOrder, OrderNotifyFromEnum.ORDER_DETAILS)"
                          >
                            <v-avatar
                              class="rounded-l"
                              size="32"
                              tile
                            >
                              <v-icon
                                :class="displayOrder.Details.DeliveryStaffNotifyAt ? 'success' : 'purple lighten-2'"
                                small
                              >
                                {{ displayOrder.Details.DeliveryStaffNotifyAt ? 'mdi-check-bold' : 'mdi-send' }}
                              </v-icon>
                            </v-avatar>

                            <div class="mx-1">
                              {{ $t('Common.Button.NotifyCollect') }}
                            </div>
                          </v-btn>
                        </template>

                        <span>{{ $t('Settings.Notification.Data.Delivery.TooltipDelivery') }}</span>
                      </v-tooltip>
                    </template>
                  </div>
                </v-card-title>

                <v-divider />

                <v-card-text
                  ref="cardOrderDetailsNotifications"
                  class=""
                  style="overflow-y: auto;"
                >
                  <order-notifications-table :notifications="displayOrder.Details.Notifications" />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions
        ref="cardOrderFooter"
        class="secondary px-1 px-sm-4"
      >
        <v-container
          class="pa-0"
          fluid
        >
          <v-row
            no-gutters
            style="align-items: center;"
          >
            <v-col
              cols="12"
              md="5"
            >
              <template v-for="(item, i) in orderCartTotals">
                <div
                  :key="i"
                  class="text-body-2 white--text px-1"
                >
                  {{
                    (item.Code === 'delivery' ?
                      $t('Order.OrderDetailsDialog.Footer.DeliveryCost') :
                      item.Code === 'coupon' ? displayOrder.SourceTypeId === SourceTypeEnum.ONLINE || displayOrder.SourceTypeId === SourceTypeEnum.MOBILE ? getTranslatedField(item, 'Name', 'Lang', true,
                                                                                                                                                                               'Title') + ': ' + item.Title : item.Title :
                      item.Code === 'extra_charge' ? `${item.Quantity} x ` + getTranslatedField(item, 'Name', 'Lang', true, 'Title') :
                      getTranslatedField(item, 'Name', 'Lang', true, 'Title')
                    )
                  }}
                  <span class="float-right pr-0 pr-md-6">
                    {{ item.Value | currency }}
                  </span>
                </div>
              </template>
            </v-col>
          </v-row>
        </v-container>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import Translatable from '@/mixins/translatable'
import OrderStatus from '@/mixins/orders/orderStatus'
import OrderSourceType from '@/mixins/orders/sourceType'
import OrderType from '@/mixins/orders/orderType'
import OrderTypeTime from '@/mixins/orders/orderTypeTime'
import BarcodeGenerator from '@/lib/barcode/BarcodeGenerator'
import BarcodePrefixEnum from '@/api/Enums/BarcodePrefixEnum'
import OrdersCommon from '@/mixins/orders/common'
import PrintButton from '@/components/common/print/PrintButton.vue'
import TableCompletedOrdersList from '@/components/restaurant/tables/orders/TableCompletedOrdersList.vue'
import TablePaymentsList from '@/components/restaurant/tables/payments/TablePaymentsList.vue'
import ReceiptsTable from '@/components/receipts/ReceiptsTable.vue'
import {formatUnitQuantity} from '@/lib/currency/currency'
import {emailIsGuest} from '@/lib/helper/helper'
import MapsCommon from '@/mixins/maps/mapsCommon'
import AddressCommon from '@/mixins/maps/addressCommon'
import VMap from '@/components/common/maps/VMap.vue'
import IssueReceiptDocumentButton from '@/components/common/receipt/IssueReceiptDocumentButton.vue'
import FiscalDocTypeEnum from '@/api/Enums/FiscalDocTypeEnum'
import NotifyTypeEnum from '@/api/Enums/NotifyTypeEnum'
import OrderPaymentsChip from '@/components/orders/common/OrderPaymentsChip.vue'
import OrderPaymentTransactionTable from '@/components/orders/common/OrderPaymentTransactionTable.vue'
import SourceTypeEnum from '@/api/Enums/SourceTypeEnum'
import OrderTypeChip from '@/components/orders/common/OrderTypeChip.vue'
import OrderStatusChip from '@/components/orders/common/OrderStatusChip.vue'
import OrderDeliveryStatusChip from '@/components/orders/common/OrderDeliveryStatusChip.vue'
import EmailNotifyFromEnum from '@/api/Enums/EmailNotifyFromEnum.js'
import OrderStatusEnum from '@/api/Enums/OrderStatusEnum.js'
import AddressTypeEnum from '@/api/Enums/AddressTypeEnum.js'
import OrderNotificationsTable from '@/components/orders/common/OrderNotificationsTable.vue'
import OrderNotifyFromEnum from '@/api/Enums/OrderNotifyFromEnum.js'
import Printer from '@/lib/print/printer.js'
import OrderPaymentTransactionHistoryTable from '@/components/orders/common/OrderPaymentTransactionHistoryTable.vue'

export default {
  name      : 'OrderDetailsDialog',
  components: {
    OrderPaymentTransactionHistoryTable,
    OrderNotificationsTable,
    OrderDeliveryStatusChip,
    OrderStatusChip,
    OrderTypeChip,
    OrderPaymentTransactionTable,
    OrderPaymentsChip,
    IssueReceiptDocumentButton,
    VMap,
    ReceiptsTable,
    TablePaymentsList,
    TableCompletedOrdersList,
    PrintButton,
    barcode: BarcodeGenerator
  },
  mixins: [AppData, Auth, MapsCommon, AddressCommon, Translatable, OrdersCommon, OrderStatus, OrderSourceType, OrderType, OrderTypeTime],
  props : {
    orderDetailsDialogVisible: {
      type   : Boolean,
      default: false
    },
    displayOrder: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {
      mapVisible     : false,
      tab            : 'tab-orders',
      transactionsTab: 'tab-transactions-active',
      tabsSliderColor: 'blue'
    }
  },
  computed: {
    OrderNotifyFromEnum() {
      return OrderNotifyFromEnum
    },
    AddressTypeEnum() {
      return AddressTypeEnum
    },
    OrderStatusEnum() {
      return OrderStatusEnum
    },
    EmailNotifyFromEnum() {
      return EmailNotifyFromEnum
    },
    SourceTypeEnum() {
      return SourceTypeEnum
    },
    NotifyTypeEnum() {
      return NotifyTypeEnum
    },
    FiscalDocTypeEnum() {
      return FiscalDocTypeEnum
    },

    canShowNotificationsTable() {
      const order = this.displayOrder.Details || {}
      return order?.Notifications?.length > 0 || this.canNotifyForStatus || this.canNotifyForCollection
    },
    canNotifyForStatus() {
      const order = this.displayOrder.Details || {}
      return !this.posUser.eft_pos && (order.CanNotify || (!order.CanNotify && order.StatusNotifyAt))
    },
    canNotifyForCollection() {
      const order = this.displayOrder.Details || {}
      return !this.posUser.eft_pos && (order.CanNotifyDelivery || (!order.CanNotifyDelivery && order.DeliveryStaffNotifyAt))
    },

    defaultActiveTab() {
      let retVal = this.tab || 'tab-orders'

      if (!this.completedCount && this.tab === 'tab-orders') retVal = 'tab-canceled'
      if (!this.unpaidCount && this.tab === 'tab-unpaid') retVal = 'tab-orders'
      if (!this.paidCount && this.tab === 'tab-paid') retVal = 'tab-orders'
      if (!this.canceledCount && this.tab === 'tab-canceled') retVal = 'tab-orders'
      if (!this.receiptsCount && this.tab === 'tab-receipts') retVal = 'tab-orders'

      if (!this.completedCount && retVal === 'tab-orders') retVal = 'tab-canceled'

      return retVal
    },

    /**
     * Returns the raw cart totals from the order details.
     * @returns {Array} List of all cart total objects.
     */
    orderTotals() {
      return this.displayOrder?.Details?.CartTotals || []
    },

    /**
     * Filters and returns the cart totals for the current order, excluding items without OrderMenuId.
     * @returns {Array} List of cart total objects.
     */
    orderCartTotals() {
      return this.orderTotals.filter(item => !item.OrderMenuId)
    },

    /**
     * Filters and returns the cart totals for specific menu items, including only items with OrderMenuId.
     * @returns {Array} List of cart item total objects.
     */
    orderCartItemsTotals() {
      return this.orderTotals.filter(item => item.OrderMenuId)
    },

    ordersList() {
      return this.displayOrder?.Details?.Table?.Completed || []
    },

    paidCount() {
      return this.displayOrder?.Details?.Table?.PaidCount || 0
    },
    canceledCount() {
      return this.displayOrder?.Details?.Table?.CanceledCount || 0
    },
    unpaidCount() {
      return this.displayOrder?.Details?.Table?.UnPaidCount || 0
    },
    completedCount() {
      return this.displayOrder?.Details?.Table?.OrdersCount || 0
    },
    receiptsCount() {
      return this.displayOrder?.Details?.Table?.ReceiptsCount || this.displayOrder?.Details?.OrderReceipts?.length || 0
    },

    ordersToday() {
      const orders = this.$DataStore?.ordersDataToday?.items || []
      return orders.filter(order => parseInt(order.StatusId) > 0)
    },
    selectedOrderReceiptsFiscalDocuments() {
      return this.displayOrder?.Details?.OrderReceipts || []
    },
    selectedOrderHasFiscalDocuments() {
      return this.selectedOrderReceiptsFiscalDocuments.length > 0
    },
    selectedOrderTransactions() {
      return this.displayOrder?.Details?.PaymentTransactions || this.displayOrder?.Details?.Payments || []
    },
    selectedOrderHasTransactions() {
      return this.selectedOrderTransactions.length > 0
    },
    isSelectedOrderToday() {
      return !!this.ordersToday.find(order => parseInt(order.Id) === parseInt(this.displayOrder.Id))
    },
    isVisible: {
      get() {
        return this.orderDetailsDialogVisible
      },
      set(val) {
        this.$emit('update:orderDetailsDialogVisible', val)
      }
    }
  },
  watch: {
    'displayOrder': {
      deep: true,
      handler() {
        this.tab = this.defaultActiveTab
      }
    },

    tab(newVal) {
      if (newVal === 'tab-orders') this.tabsSliderColor = 'blue'
      if (newVal === 'tab-unpaid') this.tabsSliderColor = 'orange'
      if (newVal === 'tab-paid') this.tabsSliderColor = 'green'
      if (newVal === 'tab-canceled') this.tabsSliderColor = 'red'
      if (newVal === 'tab-receipts') this.tabsSliderColor = 'indigo'
    },

    isVisible(newVal) {
      if (newVal) {
        this.tab = 'tab-orders'
        this.transactionsTab = 'tab-transactions-active'

        this.$nextTick(() => {
          this.tab = this.defaultActiveTab
        })

        this.$bus.$on(window.SocketCommand.Ajax.Get, this.refreshOrderDetails)
        this.$bus.$on(window.SocketCommand.Order.Payment.Transaction.OrderResult, this.onOrderPaymentTransactionResult)
        this.$bus.$on(window.SocketCommand.Receipt.Update, this.onFiscalDocumentUpdate)
        this.$bus.$on(window.SocketCommand.Order.DeliveryNotify, this.onOrderNotifyResult)
        this.$bus.$on(window.SocketCommand.Order.StatusNotify, this.onOrderNotifyResult)
      } else {
        this.$bus.$off(window.SocketCommand.Ajax.Get, this.refreshOrderDetails)
        this.$bus.$off(window.SocketCommand.Order.Payment.Transaction.OrderResult, this.onOrderPaymentTransactionResult)
        this.$bus.$off(window.SocketCommand.Receipt.Update, this.onFiscalDocumentUpdate)
        this.$bus.$off(window.SocketCommand.Order.DeliveryNotify, this.onOrderNotifyResult)
        this.$bus.$off(window.SocketCommand.Order.StatusNotify, this.onOrderNotifyResult)
      }
    },

    'displayOrder.Details': function () {
      if (!this.displayOrder) return

      if (parseInt(this.displayOrder.AddressId) > -1 && this.displayOrder?.Details && this.displayOrder?.Details?.CustomerAddress) {
        const _self = this
        _self.mapZoom = 17
        _self.mapCenter = _self.markerAddress = JSON.parse(JSON.stringify(_self.markerStore))
        _self.markerVisible = false

        setTimeout(function () {
          _self.mapCenter = _self.markerAddress = {
            lat: parseFloat(_self.displayOrder.Details.CustomerAddress.Lat) || 0,
            lng: parseFloat(_self.displayOrder.Details.CustomerAddress.Lng) || 0
          }
          _self.mapZoom = 18
          _self.markerVisible = true
          _self.mapVisible = true
        }, 250)
      } else {
        this.mapVisible = false
      }
    },
    orderDetailsDialogVisible(newVal) {
      if (newVal && parseInt(this.displayOrder.AddressId) > -1 && this.displayOrder.Details && this.displayOrder.Details.CustomerAddress) {
        // console.log('>>>>>  orderDetailsDialogVisible: ', newVal);
        const _self = this
        _self.mapZoom = 17
        _self.mapCenter = _self.markerAddress = JSON.parse(JSON.stringify(_self.markerStore))
        _self.markerVisible = false

        setTimeout(function () {
          _self.mapCenter = _self.markerAddress = {
            lat: parseFloat(_self.displayOrder.Details.CustomerAddress.Lat) || 0,
            lng: parseFloat(_self.displayOrder.Details.CustomerAddress.Lng) || 0
          }
          _self.mapZoom = 18
          _self.markerVisible = true
          _self.mapVisible = true
        }, 250)
      }

      if (!newVal) this.mapVisible = false
    }
  },
  updated() {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new Event('resize'))
  },
  methods: {
    emailIsGuest,
    formatUnitQuantity,

    /**
     *
     * @param menuId {Number}
     * @param templateId {Number}
     * @param printer {Object}
     */
    printMenuMarketing(menuId, templateId, printer) {
      if (!menuId || !printer) return

      if (this.userIsWaiter) {
        window.callAS(window.SocketCommand.Table.SendOrderMarketingForPrint, {menu_id: menuId})
      } else {
        Printer.printMenuMarketing(menuId, templateId, printer)
      }
    },

    onOrderNotifyResult(data, payload) {
      this.$bus.$emit('app-show-notification', {
        body: this.$t('Delivery.UserDashboard.Message.Notify.Success', {orderCode: payload.OrderCode}),
        type: 'success',
        icon: 'info'
      })

      this.onFiscalDocumentUpdate()
    },

    getOrderCartItemCoupons(item) {
      return this.orderCartItemsTotals.filter(c => c.OrderMenuId === item.OrderMenuId && c.Code === 'coupon')
    },

    getOrderCartItemExtraCharges(item) {
      return this.orderCartItemsTotals.filter(c => c.OrderMenuId === item.OrderMenuId && c.Code === 'extra_charge')
    },

    orderCartItemHasTotals(item) {
      return this.orderCartItemsTotals.some(c => c.OrderMenuId === item.OrderMenuId)
    },

    onFiscalDocumentUpdate() {
      if (!this.displayOrder || !this.isVisible) return

      const payload = {
        command : window.SocketCommand.commandToSocketFormat(window.SocketCommand.Order.GetDetails),
        order_id: this.displayOrder.Id
      }

      this.refreshOrderDetails(payload)
    },

    onOrderPaymentTransactionResult(payload) {
      if (!this.displayOrder || !this.isVisible) return

      payload.command = window.SocketCommand.commandToSocketFormat(window.SocketCommand.Order.GetDetails)
      this.refreshOrderDetails(payload)
    },

    refreshOrderDetails(payload) {
      if (!this.displayOrder || !this.isVisible) return

      const command = window.SocketCommand.Order.GetDetails
      const payloadCommandsArray = payload?.command ? Array.isArray(payload?.command) ? payload.command : [payload.command] : []

      if (payloadCommandsArray.length) {
        payloadCommandsArray.forEach(cmd => {
          const payloadCommand = window.SocketCommand.commandToClientFormat(cmd || '')
          if (payload && payloadCommand === command && this.displayOrder?.Id === payload?.order_id) {
            setTimeout(() => {
              this.getOrderDetails(this.displayOrder)
            }, 1000)
          }
        })
      }
    },

    getBarcode(order) {
      const barcode = {
        barcode: BarcodePrefixEnum.Order + order.Id,
        value  : order.Id
      }

      return barcode
    },
    updateOrderStatus(order) {
      this.$bus.$emit('order-update-status', order)
      this.closeDialog()
    },

    closeDialog() {
      this.$bus.$emit('hide-order-details-dialog')
    }
  }
}
</script>

<style scoped>

</style>
